<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import InputNumber from 'primevue/inputnumber';
import ProgressBar from 'primevue/progressbar';
import ProgressSpinner from 'primevue/progressspinner';
import Select from 'primevue/select';
import Tag from 'primevue/tag';
import UserAvatar from '@/components/UserAvatar.vue';
import FloatingBonusLayer from '@/components/FloatingBonusLayer.vue';
import type { QuestPartyMember, QuestState } from '@/types/api';
import { QUEST_CLASS_LABELS } from '@/types/api';
import {
  buyQuestWeapon,
  continueQuest,
  contributeQuest,
  fetchQuestState,
  joinQuest,
  submitQuestBossQueue,
  submitQuestBossSubmit,
  syncQuestFromClicker,
} from '@/lib/apiClient';
import { useAuth } from '@/composables/useAuth';
import { usePreventPageZoom } from '@/composables/usePreventPageZoom';
import { useBossFightSession } from '@/composables/useBossFightSession';
import { useFloatingBonus } from '@/composables/useFloatingBonus';
import { useBonusLoot } from '@/composables/useBonusLoot';
import { usePlaygroundPending } from '@/composables/usePlaygroundPending';

const router = useRouter();
const toast = useToast();
const { token, user } = useAuth();

const userId = computed(() => user.value?.id);
const { hasPending: hasClickerPending, flushToServer: flushClickerToServer } =
  usePlaygroundPending(userId);

const loading = ref(true);
const acting = ref(false);
const submittingTurn = ref(false);
const state = ref<QuestState | null>(null);
const selectedClass = ref<string | null>(null);
const contribHp = ref(0);
const contribGold = ref(0);
const contribLove = ref(0);
const logEl = ref<HTMLElement | null>(null);
const queueDraft = ref<number[]>([]);
const bossClickBump = ref(false);
const bossArenaRef = ref<HTMLElement | null>(null);

const classOptions = Object.entries(QUEST_CLASS_LABELS).map(([value, label]) => ({ label, value }));

const scene = computed(() => state.value?.scene ?? null);
const myMember = computed(() => state.value?.myMember ?? null);
const bossFight = computed(() => state.value?.bossFight ?? null);

const partyMembers = computed(() => {
  const byUserId = new Map<number, QuestPartyMember>();

  for (const member of state.value?.party ?? []) {
    if (!byUserId.has(member.userId)) {
      byUserId.set(member.userId, member);
    }
  }

  for (const fighter of bossFight.value?.turnOrder ?? []) {
    if (byUserId.has(fighter.userId)) continue;
    byUserId.set(fighter.userId, {
      userId: fighter.userId,
      nickname: fighter.nickname,
      userRole: 'user',
      playerClass: null,
      hp: 0,
      gold: 0,
      love: 0,
      maxHp: 0,
      maxGold: 0,
      maxLove: 0,
      weaponId: fighter.weaponId,
      fightClicks: 0,
      fightDamage: 0,
    });
  }

  return [...byUserId.values()];
});
const needsClass = computed(() => user.value?.role === 'user');
const isBossScene = computed(() => scene.value?.sceneType === 'boss');
const isShopScene = computed(() => scene.value?.sceneType === 'shop');
const isQueueManager = computed(
  () => user.value?.role === 'birthday_person' || user.value?.role === 'admin',
);

const poolProgress = computed(() => {
  if (!scene.value) return null;
  return {
    hp: pct(scene.value.hpPooled, scene.value.effectiveHpCost),
    gold: pct(scene.value.goldPooled, scene.value.effectiveGoldCost),
    love: pct(scene.value.lovePooled, scene.value.effectiveLoveCost),
  };
});

const showContribute = computed(() => {
  if (!scene.value || isShopScene.value || isBossScene.value) return false;
  return true;
});

const bossProgress = computed(() => {
  if (!state.value?.bossMaxHp || state.value.bossHp === null) return 0;
  return Math.round(((state.value.bossMaxHp - state.value.bossHp) / state.value.bossMaxHp) * 100);
});

const canManageQueue = computed(() => Boolean(bossFight.value?.canSetQueue));

const showBossWeaponShop = computed(
  () => isBossScene.value && Boolean(myMember.value) && !myMember.value?.weaponId,
);

const myFightDurationMs = computed(() =>
  myMember.value?.weaponId === 'time' ? 120_000 : 60_000,
);

const bossTurnStartedAt = computed(() => {
  const fight = bossFight.value;
  if (!fight) return null;
  if (fight.myTurnStartedAt) return fight.myTurnStartedAt;
  if (fight.canAttack && fight.fightStartedAt) return fight.fightStartedAt;
  return null;
});

const bossSessionContext = computed(() => ({
  runId: state.value?.runId ?? null,
  userId: user.value?.id,
  turnStartedAt: bossTurnStartedAt.value,
  durationMs: myFightDurationMs.value,
  canAttack: Boolean(bossFight.value?.canAttack),
  canSubmitDamage: Boolean(bossFight.value?.canSubmitDamage),
  serverRemainingMs: bossFight.value?.fightRemainingMs ?? null,
  myTurnSubmitted: Boolean(bossFight.value?.myTurnSubmitted),
}));

const {
  session: bossSession,
  isActive: bossFightActive,
  awaitingSubmit,
  timerExpired: bossTimerExpired,
  timerLabel: fightTimerLabel,
  addClick: addBossClick,
  addBonusAmount,
  markSubmitted,
  bonusTotal,
} = useBossFightSession(bossSessionContext);

const showBossSubmit = computed(
  () =>
    awaitingSubmit.value ||
    Boolean(
      bossFight.value?.canAttack &&
        bossTimerExpired.value &&
        bossSession.value &&
        !bossSession.value.submitted,
    ),
);

const { roll: rollBonus } = useBonusLoot();

const headerFightTimer = computed(() => {
  if (fightTimerLabel.value) return fightTimerLabel.value;
  const ms = bossFight.value?.fightRemainingMs;
  if (ms === null || ms === undefined) return null;
  const sec = Math.ceil(ms / 1000);
  const min = Math.floor(sec / 60);
  const rest = sec % 60;
  return `${min}:${String(rest).padStart(2, '0')}`;
});

usePreventPageZoom(bossFightActive);

const { targets: bonusTargets, handleTargetClick: handleBonusTargetClick } = useFloatingBonus({
  active: bossFightActive,
  containerRef: bossArenaRef,
  onBonus: async () => {
    const amount = await rollBonus();
    if (amount <= 0) {
      toast.add({
        severity: 'info',
        summary: 'Мимо!',
        detail: 'Бонус не выпал',
        life: 1200,
      });
      return;
    }
    addBonusAmount(amount);
    toast.add({
      severity: 'success',
      summary: `Бонус +${amount} урона!`,
      detail: 'Урон добавится при завершении атаки',
      life: 1800,
    });
  },
});

function buildQueueDraft(): number[] {
  const draft = [...partyMembers.value.map((member) => member.userId)];
  if (isQueueManager.value && user.value?.id && !draft.includes(user.value.id)) {
    draft.push(user.value.id);
  }
  return draft;
}

watch(
  () =>
    [
      state.value?.party.map((member) => member.userId).join(','),
      partyMembers.value.length,
      user.value?.id,
      user.value?.role,
      bossFight.value?.status,
    ].join('|'),
  () => {
    if (bossFight.value?.status !== 'need_queue') return;

    if (queueDraft.value.length === 0) {
      queueDraft.value = buildQueueDraft();
      return;
    }

    const merged = [...queueDraft.value];
    for (const userId of buildQueueDraft()) {
      if (!merged.includes(userId)) merged.push(userId);
    }
    queueDraft.value = merged;
  },
  { immediate: true },
);

watch(
  () => bossFight.value?.myTurnSubmitted,
  (submitted) => {
    if (submitted) markSubmitted();
  },
);

function queueNickname(userId: number) {
  const member = state.value?.party.find((entry) => entry.userId === userId);
  if (member) return member.nickname;
  if (user.value?.id === userId) return `${user.value.nickname} (вы)`;
  return `#${userId}`;
}

function pct(current: number, target: number) {
  if (target <= 0) return 100;
  return Math.min(100, Math.round((current / target) * 100));
}

function weaponLabel(id: string | null) {
  if (!id) return '—';
  return state.value?.shopWeapons.find((w) => w.id === id)?.name ?? id;
}

function logEntryClass(message: string) {
  if (message.startsWith('———')) return 'chronicle-entry--chapter';
  if (message.startsWith('✨') || message.startsWith('⚡')) return 'chronicle-entry--divine';
  if (message.startsWith('⚔️') || message.startsWith('💥')) return 'chronicle-entry--combat';
  if (message.startsWith('📋')) return 'chronicle-entry--choice';
  if (message.startsWith('🌲')) return 'chronicle-entry--join';
  if (message.startsWith('💀') || message.startsWith('🌑')) return 'chronicle-entry--fail';
  return '';
}

function formatLogMessage(message: string) {
  return message.split('\n').map((line) => line.trim()).filter(Boolean);
}

let pollTimer: ReturnType<typeof setInterval> | null = null;

function startPolling() {
  if (pollTimer) clearInterval(pollTimer);
  const intervalMs = isBossScene.value ? 3000 : 1500;
  pollTimer = setInterval(() => refresh(true), intervalMs);
}

watch(isBossScene, () => startPolling(), { immediate: true });

async function scrollLogToTop(force = false) {
  await nextTick();
  if (!logEl.value) return;
  if (force || logEl.value.scrollTop < 48) {
    logEl.value.scrollTop = 0;
  }
}

async function refresh(silent = false) {
  if (!token.value) return;
  try {
    const prevLen = state.value?.log.length ?? 0;
    state.value = (await fetchQuestState(token.value)).state;
    if ((state.value?.log.length ?? 0) !== prevLen) {
      await scrollLogToTop();
    }
  } catch (error) {
    if (!silent) {
      toast.add({
        severity: 'error',
        summary: 'Ошибка',
        detail: error instanceof Error ? error.message : 'Не удалось загрузить хронику',
        life: 3000,
      });
    }
  }
}

watch(() => state.value?.log.length, () => scrollLogToTop());

onMounted(async () => {
  await refresh();
  loading.value = false;
  await scrollLogToTop(true);
  startPolling();
});

onUnmounted(() => {
  if (pollTimer) clearInterval(pollTimer);
});

async function doJoin() {
  if (!token.value) return;
  acting.value = true;
  try {
    state.value = (
      await joinQuest(token.value, needsClass.value ? selectedClass.value ?? undefined : undefined)
    ).state;
    await scrollLogToTop(true);
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Туман не пускает',
      detail: error instanceof Error ? error.message : 'Ошибка',
      life: 3500,
    });
  } finally {
    acting.value = false;
  }
}

async function doContribute() {
  if (!token.value) return;
  acting.value = true;
  try {
    state.value = (
      await contributeQuest(token.value, {
        hp: contribHp.value,
        gold: contribGold.value,
        love: contribLove.value,
      })
    ).state;
    contribHp.value = 0;
    contribGold.value = 0;
    contribLove.value = 0;
    await scrollLogToTop(true);
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Котёл не принял',
      detail: error instanceof Error ? error.message : 'Ошибка',
      life: 3500,
    });
  } finally {
    acting.value = false;
  }
}

async function doSync() {
  if (!token.value) return;
  acting.value = true;
  try {
    let flushedFromClicker = false;
    if (hasClickerPending.value) {
      flushedFromClicker = await flushClickerToServer(token.value);
    }

    state.value = (await syncQuestFromClicker(token.value)).state;
    await scrollLogToTop(true);
    toast.add({
      severity: 'success',
      summary: 'Пожелания перенесены',
      detail: flushedFromClicker
        ? 'Кликер сохранён на сервер и очки добавлены к ресурсам в квесте'
        : 'Новые очки из кликера добавлены к вашим ресурсам',
      life: 3000,
    });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Не удалось забрать',
      detail: error instanceof Error ? error.message : 'Ошибка',
      life: 3500,
    });
  } finally {
    acting.value = false;
  }
}

async function doContinue() {
  if (!token.value) return;
  acting.value = true;
  try {
    state.value = (await continueQuest(token.value)).state;
    queueDraft.value = [];
    await scrollLogToTop(true);
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Тропа не открылась',
      detail: error instanceof Error ? error.message : 'Ошибка',
      life: 3500,
    });
  } finally {
    acting.value = false;
  }
}

async function doBuyWeapon(weaponId: string) {
  if (!token.value) return;
  acting.value = true;
  try {
    state.value = (await buyQuestWeapon(token.value, weaponId)).state;
    await scrollLogToTop(true);
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Торговец качает головой',
      detail: error instanceof Error ? error.message : 'Ошибка',
      life: 3500,
    });
  } finally {
    acting.value = false;
  }
}

function moveQueue(index: number, delta: number) {
  const next = index + delta;
  if (next < 0 || next >= queueDraft.value.length) return;
  const copy = [...queueDraft.value];
  [copy[index], copy[next]] = [copy[next], copy[index]];
  queueDraft.value = copy;
}

async function doSetQueue() {
  if (!token.value) return;
  acting.value = true;
  try {
    const order = buildQueueDraft();
    queueDraft.value = order;
    state.value = (await submitQuestBossQueue(token.value, order)).state;
    await scrollLogToTop(true);
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Очередь не сохранена',
      detail: error instanceof Error ? error.message : 'Ошибка',
      life: 3500,
    });
  } finally {
    acting.value = false;
  }
}

async function submitBossTurn() {
  if (!token.value || !bossSession.value || submittingTurn.value || bossSession.value.submitted) {
    return;
  }

  submittingTurn.value = true;
  try {
    state.value = (
      await submitQuestBossSubmit(token.value, {
        clicks: bossSession.value.clicks,
        bonusAmounts: bossSession.value.bonusAmounts,
        turnStartedAt: bossSession.value.turnStartedAt,
      })
    ).state;
    markSubmitted();
    await scrollLogToTop(true);
  } catch (error) {
    await refresh(true);
    toast.add({
      severity: 'error',
      summary: 'Урон не зафиксирован',
      detail: error instanceof Error ? error.message : 'Ошибка',
      life: 3000,
    });
  } finally {
    submittingTurn.value = false;
  }
}

function handleBossClick() {
  if (!bossFightActive.value) return;

  addBossClick();
  bossClickBump.value = true;
  window.setTimeout(() => {
    bossClickBump.value = false;
  }, 120);
}

function goPlayground() {
  router.push({ name: 'playground' });
}

function goTriWishes() {
  router.push({ name: 'tri-wishes' });
}
</script>

<template>
  <div class="forest">
    <header class="forest__top">
      <Button icon="pi pi-arrow-left" label="Игральня" text class="forest__back" @click="goPlayground" />
      <div class="forest__brand">
        <span class="forest__brand-icon">🌲</span>
        <div>
          <h1>Лес Трёх Желаний</h1>
          <p>Магический отряд · хроника пути</p>
        </div>
      </div>
    </header>

    <ProgressSpinner v-if="loading" class="forest__spinner" />

    <template v-else-if="state">
      <section v-if="state.status === 'idle'" class="forest-idle">
        <p class="forest-idle__icon">🌫️</p>
        <h2>Портал ещё закрыт</h2>
        <p>
          Божественный Создатель ещё не открыл врата в лес. Копите пожелания в «Три Желания» — они
          станут вашей силой, золотом и узами, когда тропа проявится.
        </p>
      </section>

      <section v-else-if="state.status === 'failed'" class="forest-idle forest-idle--fail">
        <p class="forest-idle__icon">🌑</p>
        <h2>Лес погрузился в сон</h2>
        <p>Кампания остановлена. Создатель может открыть портал заново — а герои вернутся в кликер.</p>
        <div v-if="state.log.length" ref="logEl" class="chronicle__body chronicle__body--compact">
          <article
            v-for="entry in state.log"
            :key="entry.id"
            class="chronicle-entry"
            :class="logEntryClass(entry.message)"
          >
            <time>{{ new Date(entry.createdAt).toLocaleTimeString('ru-RU') }}</time>
            <div class="chronicle-entry__text">
              <p v-for="(line, idx) in formatLogMessage(entry.message)" :key="idx">{{ line }}</p>
            </div>
          </article>
        </div>
      </section>

      <section v-else-if="state.status === 'won'" class="forest-idle forest-idle--win">
        <p class="forest-idle__icon">✨</p>
        <h2>Победа магического отряда</h2>
        <p v-if="state.finalNote" class="forest-idle__note">{{ state.finalNote }}</p>
      </section>

      <div v-else-if="state.active" class="forest-layout">
        <main class="chronicle">
          <header class="chronicle__head">
            <div class="chronicle__head-main">
              <Tag
                v-if="scene"
                :value="`Глава ${(state.currentSceneIndex ?? 0) + 1}: ${scene.title}`"
                severity="success"
                rounded
              />
            </div>
            <div v-if="state.bossHp !== null" class="chronicle__boss">
              <span>👹 Хранитель</span>
              <ProgressBar :value="bossProgress" :show-value="false" />
              <small>{{ state.bossHp }} / {{ state.bossMaxHp }}</small>
            </div>
            <div v-if="isBossScene && bossFight?.currentFighterNickname" class="chronicle__choice-banner">
              <strong>⚔️ Сейчас в бою</strong>
              <p>
                {{ bossFight.currentFighterNickname }}
                <template v-if="headerFightTimer"> — ⏱ {{ headerFightTimer }}</template>
              </p>
            </div>
          </header>

          <div ref="logEl" class="chronicle__body">
            <article
              v-for="entry in state.log"
              :key="entry.id"
              class="chronicle-entry"
              :class="logEntryClass(entry.message)"
            >
              <time>{{ new Date(entry.createdAt).toLocaleTimeString('ru-RU') }}</time>
              <div class="chronicle-entry__text">
                <p v-for="(line, idx) in formatLogMessage(entry.message)" :key="idx">{{ line }}</p>
              </div>
            </article>
            <p v-if="state.log.length > 3" class="chronicle__scroll-hint">↓ ниже — более ранние записи</p>
            <p v-if="state.log.length === 0" class="chronicle__empty">Хроника ждёт первых строк…</p>
          </div>
        </main>

        <aside class="tools">
          <section v-if="!myMember" class="tools__block tools__block--join">
            <h3>Войти в отряд</h3>
            <p class="tools__hint">
              <template v-if="isBossScene && bossFight?.fightActive">
                Битва уже идёт — можно вступить прямо сейчас! Лес добавит вас в очередь атаки.
                Если в кликере ещё нет очков, дадим стартовый запас 💚10 💰10 ❤️10.
              </template>
              <template v-else-if="isBossScene">
                Хранитель ждёт — присоединяйтесь до начала боя или успейте влиться, когда битва
                уже пойдёт.
              </template>
              <template v-else>
                Ваши пожелания из кликера станут магическими ресурсами. Новые герои могут
                присоединиться в любой момент — лес запишет это в хронику.
              </template>
            </p>
            <label v-if="needsClass" class="tools__field">
              <span>Путь героя</span>
              <Select
                v-model="selectedClass"
                :options="classOptions"
                option-label="label"
                option-value="value"
                placeholder="Выберите класс"
                fluid
              />
            </label>
            <Button
              label="Шагнуть из тумана"
              icon="pi pi-sign-in"
              class="tools__btn"
              :loading="acting"
              :disabled="needsClass && !selectedClass"
              @click="doJoin"
            />
          </section>

          <section v-if="showContribute && scene" class="tools__block">
            <h3>Общий котёл</h3>
            <div class="tools__pool">
              <div class="tools__pool-row">
                <span>💚 {{ scene.hpPooled }}/{{ scene.effectiveHpCost }}</span>
                <ProgressBar v-if="poolProgress" :value="poolProgress.hp" :show-value="false" />
              </div>
              <div class="tools__pool-row">
                <span>💰 {{ scene.goldPooled }}/{{ scene.effectiveGoldCost }}</span>
                <ProgressBar v-if="poolProgress" :value="poolProgress.gold" :show-value="false" />
              </div>
              <div class="tools__pool-row">
                <span>❤️ {{ scene.lovePooled }}/{{ scene.effectiveLoveCost }}</span>
                <ProgressBar v-if="poolProgress" :value="poolProgress.love" :show-value="false" />
              </div>
            </div>
          </section>

          <section v-if="isBossScene && canManageQueue" class="tools__block tools__block--dice">
            <h3>📋 Очередь атаки</h3>
            <p class="tools__hint">Именинник или админ выстраивает порядок — кто первым бьёт Хранителя.</p>
            <p v-if="partyMembers.length !== queueDraft.length" class="tools__hint tools__hint--warn">
              В отряде {{ partyMembers.length }} героев — убедитесь, что все в очереди, прежде чем начать.
            </p>
            <ul class="tools__queue">
              <li v-for="(userId, index) in queueDraft" :key="userId" class="tools__queue-row">
                  <span>{{ index + 1 }}. {{ queueNickname(userId) }}</span>
                <span class="tools__queue-actions">
                  <Button icon="pi pi-arrow-up" text size="small" @click="moveQueue(index, -1)" />
                  <Button icon="pi pi-arrow-down" text size="small" @click="moveQueue(index, 1)" />
                </span>
              </li>
            </ul>
            <Button
              label="Начать битву"
              icon="pi pi-play"
              class="tools__btn"
              :loading="acting"
              @click="doSetQueue"
            />
          </section>

          <template v-if="myMember">
            <section class="tools__block">
              <h3>{{ myMember.nickname }}</h3>
              <div class="tools__chips">
                <span class="chip chip--hp">💚 {{ myMember.hp }}</span>
                <span class="chip chip--gold">💰 {{ myMember.gold }}</span>
                <span class="chip chip--love">❤️ {{ myMember.love }}</span>
              </div>
              <p v-if="myMember.weaponId" class="tools__hint tools__hint--win">
                Оружие: {{ weaponLabel(myMember.weaponId) }}
              </p>
              <p v-if="myMember.playerClass" class="tools__class">
                {{ QUEST_CLASS_LABELS[myMember.playerClass] }}
              </p>
              <p v-if="!isBossScene" class="tools__hint">
                Не хватает сил? Покликайте в «Три Желания» и заберите новые очки сюда.
              </p>
              <p v-if="hasClickerPending" class="tools__hint tools__hint--win">
                В кликере есть несохранённые очки — «Забрать из кликера» отправит их на сервер и перенесёт в квест.
              </p>
              <div v-if="!isBossScene || showBossWeaponShop" class="tools__sync-row">
                <Button
                  v-if="!isBossScene || showBossWeaponShop"
                  label="Забрать из кликера"
                  icon="pi pi-download"
                  size="small"
                  outlined
                  class="tools__btn"
                  :loading="acting"
                  @click="doSync"
                />
                <Button
                  v-if="!isBossScene"
                  label="Три Желания"
                  icon="pi pi-external-link"
                  size="small"
                  text
                  @click="goTriWishes"
                />
              </div>
            </section>

            <section v-if="showBossWeaponShop" class="tools__block">
              <h3>🛡️ Быстрая лавка</h3>
              <p class="tools__hint">
                Вы вступили в разгар боя — успейте взять оружие до своего хода.
              </p>
              <div
                v-for="weapon in state.shopWeapons"
                :key="weapon.id"
                class="tools__weapon"
              >
                <strong>{{ weapon.emoji }} {{ weapon.name }}</strong>
                <span class="tools__hint">{{ weapon.description }}</span>
                <span>💰 {{ weapon.goldCost }}</span>
                <Button
                  label="Взять"
                  size="small"
                  :loading="acting"
                  :disabled="myMember.gold < weapon.goldCost"
                  @click="doBuyWeapon(weapon.id)"
                />
              </div>
            </section>

            <section v-if="isShopScene" class="tools__block">
              <h3>🛡️ Оружейная</h3>
              <p class="tools__hint">Выберите одно оружие перед битвой с Хранителем.</p>
              <div
                v-for="weapon in state.shopWeapons"
                :key="weapon.id"
                class="tools__weapon"
              >
                <strong>{{ weapon.emoji }} {{ weapon.name }}</strong>
                <span class="tools__hint">{{ weapon.description }}</span>
                <span>💰 {{ weapon.goldCost }}</span>
                <Button
                  v-if="!myMember.weaponId"
                  label="Взять"
                  size="small"
                  :loading="acting"
                  :disabled="myMember.gold < weapon.goldCost"
                  @click="doBuyWeapon(weapon.id)"
                />
              </div>
              <p v-if="myMember.weaponId" class="tools__hint tools__hint--win">
                Вы вооружены: {{ weaponLabel(myMember.weaponId) }}
              </p>
              <Button
                label="К битве с Хранителем"
                icon="pi pi-arrow-right"
                class="tools__btn"
                :loading="acting"
                @click="doContinue"
              />
            </section>

            <section
              v-if="isBossScene && (bossFight?.canAttack || bossFight?.canSubmitDamage)"
              class="tools__block tools__block--dice"
            >
              <h3 v-if="bossFight?.canAttack">⚔️ Ваша атака!</h3>
              <h3 v-else>📤 Зафиксировать урон</h3>
              <p class="tools__hint">
                <template v-if="bossFight?.canAttack && bossFightActive">
                  Кликайте как в «Три Желания» — 1 клик = −1 HP боссу. Ловите ✨ цели — случайный бонус из таблицы.
                  <template v-if="fightTimerLabel"> Осталось {{ fightTimerLabel }}.</template>
                </template>
                <template v-else-if="bossFight?.canSubmitDamage && bossFight.currentFighterUserId !== user?.id">
                  Эстафета перешла дальше — отправьте накопленный урон, пока он не потерялся.
                </template>
                <template v-else-if="bossFight?.canSubmitDamage">
                  Время вышло! Проверьте удары и нажмите «Зафиксировать урон».
                </template>
              </p>
              <p class="tools__hint">
                Удары: {{ bossSession?.clicks ?? 0 }}
                <template v-if="bonusTotal"> · Бонусный урон: +{{ bonusTotal }}</template>
              </p>
              <div v-if="bossFight?.canAttack && bossFightActive" ref="bossArenaRef" class="boss-arena">
                <FloatingBonusLayer :targets="bonusTargets" @hit="handleBonusTargetClick" />
                <div
                  class="boss-clicker"
                  :class="{ 'boss-clicker--bump': bossClickBump }"
                  role="button"
                  tabindex="0"
                  @click="handleBossClick"
                  @keydown.enter="handleBossClick"
                  @keydown.space.prevent="handleBossClick"
                >
                  ⚔️ БИТЬ!
                </div>
              </div>
              <Button
                v-if="showBossSubmit"
                label="Зафиксировать урон"
                icon="pi pi-check"
                severity="success"
                class="tools__btn tools__btn--end-turn"
                :loading="submittingTurn"
                @click="submitBossTurn"
              />
              <p v-else-if="submittingTurn" class="tools__hint tools__hint--win">Отправка урона…</p>
            </section>

            <section
              v-else-if="isBossScene && bossFight?.fightActive && myMember"
              class="tools__block"
            >
              <h3>👀 Наблюдение</h3>
              <p class="tools__hint">
                Сейчас атакует {{ bossFight.currentFighterNickname ?? '…' }}.
                HP Хранителя: {{ state.bossHp }} / {{ state.bossMaxHp }}.
              </p>
              <p v-if="bossFight.myQueuePosition" class="tools__hint tools__hint--win">
                Вы в очереди: №{{ bossFight.myQueuePosition }} из {{ bossFight.turnOrder.length }}.
                Дождитесь своего хода — лес позовёт вас автоматически.
              </p>
              <p v-else class="tools__hint">
                Очередь обновляется… Обновите страницу, если ждёте дольше нескольких секунд.
              </p>
              <ul v-if="bossFight.turnOrder.length" class="tools__queue tools__queue--readonly">
                <li
                  v-for="(fighter, index) in bossFight.turnOrder"
                  :key="fighter.userId"
                  class="tools__queue-row"
                  :class="{ 'tools__queue-row--active': fighter.userId === bossFight.currentFighterUserId }"
                >
                  <span>
                    {{ index + 1 }}. {{ fighter.nickname }}
                    <template v-if="fighter.userId === user?.id"> (вы)</template>
                    <template v-if="fighter.userId === bossFight.currentFighterUserId"> ⚔️</template>
                  </span>
                </li>
              </ul>
            </section>

            <section v-if="showContribute" class="tools__block">
              <h3>Влить в котёл</h3>
              <div class="tools__contrib">
                <label>💚 <InputNumber v-model="contribHp" :min="0" :max="myMember.hp" fluid /></label>
                <label>💰 <InputNumber v-model="contribGold" :min="0" :max="myMember.gold" fluid /></label>
                <label>❤️ <InputNumber v-model="contribLove" :min="0" :max="myMember.love" fluid /></label>
              </div>
              <Button
                label="Отдать силу лесу"
                class="tools__btn"
                :loading="acting"
                @click="doContribute"
              />
            </section>

            <section v-if="partyMembers.length" class="tools__block">
              <h3>Отряд · {{ partyMembers.length }}</h3>
              <div class="tools__party">
                <div v-for="member in partyMembers" :key="member.userId" class="tools__party-row">
                  <UserAvatar :nickname="member.nickname" :avatar="null" size="normal" />
                  <span>{{ member.nickname }}</span>
                </div>
              </div>
            </section>
          </template>
        </aside>
      </div>
    </template>
  </div>
</template>

<style scoped>
.forest {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  max-width: 1100px;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  overflow: hidden;
  flex: 1;
}

.forest__top {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.forest__back {
  flex-shrink: 0;
}

.forest__brand {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  min-width: 0;
}

.forest__brand-icon {
  font-size: 1.65rem;
  line-height: 1;
  flex-shrink: 0;
}

.forest__brand h1 {
  margin: 0;
  font-size: 1.15rem;
  color: #064e3b;
  font-family: Georgia, 'Times New Roman', serif;
}

.forest__brand p {
  margin: 0.1rem 0 0;
  font-size: 0.78rem;
  color: #047857;
}

.forest__spinner {
  margin: auto;
  flex-shrink: 0;
}

.forest-idle {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 1.5rem 1.25rem;
  border-radius: 1.25rem;
  background: linear-gradient(160deg, #ecfdf5 0%, #d1fae5 100%);
  border: 1px solid #6ee7b7;
  text-align: center;
}

.forest-idle--fail {
  background: linear-gradient(160deg, #fef2f2, #fce7f3);
  border-color: #fca5a5;
}

.forest-idle--win {
  background: linear-gradient(160deg, #ecfdf5, #fef9c3);
  border-color: #fcd34d;
}

.forest-idle__icon {
  font-size: 2.5rem;
  margin: 0 0 0.5rem;
}

.forest-idle h2 {
  margin: 0 0 0.65rem;
  color: #065f46;
  font-family: Georgia, 'Times New Roman', serif;
}

.forest-idle p {
  margin: 0;
  color: #047857;
  line-height: 1.6;
  max-width: 36rem;
  margin-inline: auto;
}

.forest-idle__note {
  margin-top: 1rem !important;
  font-size: 1.15rem;
  font-style: italic;
  color: #065f46 !important;
}

.forest-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 250px;
  grid-template-rows: minmax(0, 1fr);
  gap: 0.65rem;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.chronicle {
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: 100%;
  overflow: hidden;
  border-radius: 1.25rem;
  border: 1px solid #065f4633;
  box-shadow: 0 16px 40px rgb(6 78 59 / 12%);
  background: linear-gradient(180deg, #022c22 0%, #064e3b 40%, #065f46 100%);
}

.chronicle__head {
  padding: 0.85rem 1rem;
  background: rgb(0 0 0 / 25%);
  border-bottom: 1px solid rgb(255 255 255 / 8%);
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: flex-start;
  justify-content: space-between;
  flex-shrink: 0;
  flex-direction: column;
}

.chronicle__choice-banner {
  width: 100%;
  padding: 0.65rem 0.75rem;
  border-radius: 0.75rem;
  background: rgb(252 211 77 / 15%);
  border: 1px solid rgb(252 211 77 / 45%);
}

.chronicle__choice-banner strong {
  display: block;
  color: #fef9c3;
  margin-bottom: 0.35rem;
}

.chronicle__choice-banner p {
  margin: 0 0 0.25rem;
  color: #ecfdf5;
  line-height: 1.45;
  font-size: 0.9rem;
}

.chronicle__choice-banner small {
  color: #fcd34d;
  font-size: 0.75rem;
  opacity: 0.9;
}

.chronicle__choice-banner--done {
  background: rgb(110 231 183 / 12%);
  border-color: rgb(110 231 183 / 35%);
}

.chronicle__choice-banner--fail {
  background: rgb(248 113 113 / 12%);
  border-color: rgb(248 113 113 / 40%);
}

.chronicle-entry--dice {
  border-left-color: #fcd34d;
}

.chronicle__head-main {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  align-items: center;
  width: 100%;
  justify-content: space-between;
}

.chronicle__timer {
  color: #fcd34d;
  font-size: 0.85rem;
  font-weight: 700;
}

.chronicle__boss {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 180px;
  color: #fecaca;
  font-size: 0.8rem;
}

.chronicle__boss :deep(.p-progressbar) {
  flex: 1;
  height: 6px;
}

.chronicle__body {
  flex: 1 1 auto;
  min-height: 0;
  max-height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  padding: 0.75rem 1rem 0.85rem;
  padding-bottom: max(0.85rem, env(safe-area-inset-bottom, 0));
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  scroll-behavior: smooth;
}

.chronicle__scroll-hint {
  margin: 0 0 0.15rem;
  text-align: center;
  font-size: 0.72rem;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #86efac;
  opacity: 0.65;
  flex-shrink: 0;
}

.chronicle__body--compact {
  max-height: 240px;
  margin-top: 1.25rem;
  border-radius: 0.75rem;
  background: rgb(0 0 0 / 15%);
}

.chronicle__empty {
  margin: auto;
  color: #a7f3d0;
  font-style: italic;
  opacity: 0.8;
  flex-shrink: 0;
}

.chronicle-entry {
  display: grid;
  grid-template-columns: 58px minmax(0, 1fr);
  gap: 0.65rem;
  padding: 0.65rem 0.75rem;
  border-radius: 0.75rem;
  background: rgb(255 255 255 / 6%);
  border-left: 3px solid rgb(167 243 208 / 35%);
  flex-shrink: 0;
}

.chronicle-entry time {
  font-size: 0.72rem;
  color: #86efac;
  opacity: 0.85;
  padding-top: 0.15rem;
}

.chronicle-entry__text p {
  margin: 0 0 0.35rem;
  color: #ecfdf5;
  line-height: 1.55;
  font-size: 0.92rem;
}

.chronicle-entry__text p:last-child {
  margin-bottom: 0;
}

.chronicle-entry--chapter {
  background: rgb(252 211 77 / 12%);
  border-left-color: #fcd34d;
}

.chronicle-entry--chapter .chronicle-entry__text p:first-child {
  font-family: Georgia, 'Times New Roman', serif;
  font-weight: 700;
  color: #fef9c3;
}

.chronicle-entry--divine {
  border-left-color: #c4b5fd;
  background: rgb(196 181 253 / 10%);
}

.chronicle-entry--combat {
  border-left-color: #fca5a5;
}

.chronicle-entry--join {
  border-left-color: #6ee7b7;
}

.chronicle-entry--fail {
  border-left-color: #f87171;
  background: rgb(248 113 113 / 10%);
}

.tools {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  min-height: 0;
  max-height: 100%;
  overflow: hidden;
}

.tools__block {
  padding: 0.65rem 0.75rem;
  border-radius: 0.85rem;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  flex-shrink: 0;
}

.tools__block--join {
  background: linear-gradient(145deg, #ecfdf5, #fef9c3);
  border-color: #fcd34d;
}

.tools__block h3 {
  margin: 0 0 0.35rem;
  font-size: 0.75rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #065f46;
  font-weight: 800;
}

.tools__hint {
  margin: 0 0 0.45rem;
  font-size: 0.76rem;
  line-height: 1.35;
  color: #047857;
}

.tools__field {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  margin-bottom: 0.65rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #065f46;
}

.tools__btn {
  width: 100%;
}

.tools__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.chip {
  padding: 0.25rem 0.55rem;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 700;
}

.chip--hp {
  background: #dcfce7;
  color: #166534;
}

.chip--gold {
  background: #fef3c7;
  color: #92400e;
}

.chip--love {
  background: #fce7f3;
  color: #9d174d;
}

.tools__sync-row {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-top: 0.45rem;
}

.tools__class {
  margin: 0.45rem 0 0;
  font-size: 0.8rem;
  color: #047857;
}

.tools__pool {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.tools__pool-row {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  font-size: 0.78rem;
  color: #047857;
  font-weight: 600;
}

.tools__pool-row :deep(.p-progressbar) {
  height: 5px;
}

.tools__inline {
  display: flex;
  gap: 0.35rem;
}

.tools__shop {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.85rem;
  color: #065f46;
}

.tools__weapon {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.5rem 0.55rem;
  margin-bottom: 0.35rem;
  border-radius: 0.65rem;
  background: #fff;
  border: 1px solid #bbf7d0;
  font-size: 0.82rem;
  color: #065f46;
}

.tools__queue {
  margin: 0 0 0.5rem;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.tools__queue-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.35rem;
  font-size: 0.82rem;
  color: #065f46;
  padding: 0.35rem 0.45rem;
  border-radius: 0.5rem;
  background: rgb(255 255 255 / 70%);
}

.tools__queue-row--active {
  background: #dcfce7;
  font-weight: 700;
  border: 1px solid #86efac;
}

.tools__queue--readonly .tools__queue-row {
  justify-content: flex-start;
}

.tools__queue-actions {
  display: flex;
  gap: 0;
  flex-shrink: 0;
}

.tools__btn--end-turn {
  margin-top: 0.5rem;
}

.boss-arena {
  position: relative;
  margin-top: 0.35rem;
  min-height: 96px;
}

.boss-clicker {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 88px;
  margin-top: 0.35rem;
  border-radius: 1rem;
  font-size: 1.35rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  color: #7f1d1d;
  background: linear-gradient(145deg, #fecaca, #f87171);
  border: 2px solid #dc2626;
  box-shadow: 0 6px 0 #991b1b, 0 10px 24px rgb(127 29 29 / 25%);
  cursor: pointer;
  touch-action: manipulation;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  transition: transform 0.08s ease, box-shadow 0.08s ease;
}

.boss-clicker:active,
.boss-clicker--bump {
  transform: scale(0.97) translateY(3px);
  box-shadow: 0 2px 0 #991b1b, 0 4px 12px rgb(127 29 29 / 20%);
}

.tools__contrib {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.35rem;
  margin-bottom: 0.5rem;
}

.tools__contrib label {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  font-size: 0.75rem;
}

.chronicle-entry--choice {
  border-left-color: #fcd34d;
  background: rgb(252 211 77 / 12%);
}

.tools__block--dice {
  background: linear-gradient(145deg, #fef3c7, #fff7ed);
  border-color: #fbbf24;
}

.tools__block--dice-fail {
  background: #fef2f2;
  border-color: #fca5a5;
}

.tools__hint--win {
  color: #047857 !important;
  font-weight: 700;
}

.tools__dice-log {
  margin: 0.5rem 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.8rem;
  color: #065f46;
}

.tools__dice-log--win {
  font-weight: 700;
  color: #047857;
}

.tools__block--choice {
  background: linear-gradient(145deg, #fef9c3, #ecfdf5);
  border-color: #fcd34d;
}

.tools__block--choice-done {
  background: #fefce8;
  border-color: #fde047;
}

.tools__hint--discuss {
  font-style: italic;
  color: #92400e !important;
}

.tools__hint--join-first {
  color: #b45309 !important;
  font-weight: 700;
}

.tools__choices {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.choice-btn {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.55rem 0.65rem;
  border-radius: 0.65rem;
  border: 1px solid #86efac;
  background: #fff;
  text-align: left;
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}

.choice-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgb(6 78 59 / 12%);
}

.choice-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.choice-btn strong {
  font-size: 0.85rem;
  color: #065f46;
}

.choice-btn span {
  font-size: 0.74rem;
  line-height: 1.35;
  color: #047857;
}

.tools__party {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  max-height: none;
  overflow: visible;
}

.tools__party-row {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.82rem;
  color: #065f46;
}

@media (max-width: 1024px) {
  .forest-layout {
    grid-template-columns: 1fr;
    grid-template-rows: minmax(0, 1fr) auto;
  }
}

@media (max-width: 900px) {
  .forest {
    gap: 0.35rem;
  }

  .forest__top {
    gap: 0.35rem;
  }

  .forest__brand h1 {
    font-size: 1rem;
  }

  .forest__brand p {
    display: none;
  }

  .forest__back :deep(.p-button-label) {
    display: none;
  }

  .forest-layout {
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
    min-height: 0;
    overflow: hidden;
  }

  .chronicle {
    flex: 1 1 auto;
    width: 100%;
    min-height: 0;
    height: clamp(210px, 46dvh, 480px);
    max-height: min(52dvh, calc(100dvh - 13rem));
  }

  .chronicle__body {
    padding: 0.65rem 0.75rem 0.75rem;
    touch-action: pan-y;
  }

  .tools {
    order: 2;
    flex: 0 1 auto;
    width: 100%;
    min-height: 0;
    max-height: min(42dvh, calc(100dvh - 16rem));
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    scroll-padding-bottom: env(safe-area-inset-bottom, 0.75rem);
    padding-bottom: env(safe-area-inset-bottom, 0);
    gap: 0.4rem;
  }

  .chronicle-entry {
    grid-template-columns: 50px minmax(0, 1fr);
    gap: 0.5rem;
  }

  .chronicle-entry__text p {
    font-size: 0.88rem;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .chronicle__boss {
    width: 100%;
    min-width: 0;
  }

  .choice-btn {
    min-height: 44px;
    touch-action: manipulation;
  }

  .boss-clicker {
    min-height: 72px;
    font-size: 1.15rem;
  }

  .tools__btn :deep(.p-button) {
    min-height: 44px;
    touch-action: manipulation;
  }
}

@media (max-width: 480px) {
  .forest__brand-icon {
    font-size: 1.35rem;
  }

  .chronicle {
    border-radius: 1rem;
    height: clamp(190px, 42dvh, 360px);
    max-height: min(48dvh, calc(100dvh - 14rem));
  }

  .chronicle-entry {
    grid-template-columns: 1fr;
    gap: 0.15rem;
    padding: 0.55rem 0.65rem;
  }

  .chronicle-entry time {
    font-size: 0.68rem;
    padding-top: 0;
  }

  .chronicle__head {
    padding: 0.6rem 0.7rem;
    gap: 0.5rem;
  }

  .chronicle__choice-banner {
    padding: 0.55rem 0.65rem;
  }

  .chronicle__choice-banner p {
    font-size: 0.84rem;
  }

  .tools {
    max-height: min(46dvh, calc(100dvh - 12rem));
  }

  .tools__block {
    padding: 0.55rem 0.65rem;
  }

  .tools__contrib {
    grid-template-columns: 1fr;
    gap: 0.45rem;
  }

  .tools__inline {
    flex-direction: column;
    align-items: stretch;
  }

  .tools__party {
    max-height: none;
  }

  .forest-idle {
    padding: 1rem 0.85rem;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
}

@media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
  .forest-layout {
    display: grid;
    grid-template-columns: minmax(0, 1.15fr) minmax(220px, 0.85fr);
    grid-template-rows: minmax(0, 1fr);
    gap: 0.55rem;
  }

  .chronicle {
    height: auto;
    max-height: 100%;
  }

  .tools {
    order: unset;
    max-height: 100%;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
}

@media (hover: none) and (pointer: coarse) {
  .choice-btn:hover:not(:disabled) {
    transform: none;
    box-shadow: none;
  }

  .choice-btn:active:not(:disabled) {
    transform: scale(0.98);
  }
}
</style>
